import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardHeader from '@mui/material/CardHeader';
import Collapse from '@mui/material/Collapse';
import Grid from '@mui/material/Grid';
import { styled } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import React, { ReactNode, useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import CollapseExpandButton from './CollapseExpandButton';
import { useExpand } from './ExpandContext';
import PropertiesButton from './PropertiesButton';

const PREFIX = 'GroupContainer';

const classes = {
  group: `${PREFIX}-group`,
  children: `${PREFIX}-children`,
  groupHeader: `${PREFIX}-groupHeader`,
  icon: `${PREFIX}-icon`,
  headerContent: `${PREFIX}-headerContent`,
  subheader: `${PREFIX}-subheader`,
  spacer: `${PREFIX}-spacer`
};

const StyledCard = styled(Card)(({ theme }) => ({
  [`&.${classes.group}`]: {
    color: theme.palette.text.primary
  },

  [`& .${classes.children}`]: {},

  [`& .${classes.groupHeader}`]: {
    display: 'flex',
    '&:hover ': {
      '& .GroupContainer-icon': {
        visibility: 'visible'
      }
    }
  },

  [`& .${classes.icon}`]: {
    visibility: 'hidden',
    height: '32px'
  },

  [`& .${classes.headerContent}`]: {
    minWidth: 0,
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column'
  },

  [`& .${classes.subheader}`]: {
    maxWidth: 960,
    margin: 'auto'
  },
  [`& .${classes.spacer}`]: {
    width: '100%'
  }
}));

interface GroupContainerProps {
  title: string;
  key: string;
  subheader: ReactNode;
  children: ReactNode;
  onPropertiesClick?: () => void;
}

const GroupContainer = ({
  title,
  onPropertiesClick,
  children,
  subheader,
  key
}: GroupContainerProps) => {
  const { isAllExpanded, resetAll } = useExpand();
  const { search } = useLocation();
  const searchParams = new URLSearchParams(search);
  const isCollapsed = searchParams.get('collapsedGroups');

  const [isExpanded, setIsExpanded] = useState<boolean>(!isCollapsed);

  useEffect(() => {
    if (isAllExpanded !== undefined) setIsExpanded(isAllExpanded.state);
  }, [isAllExpanded]);

  const iconOnClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    e.preventDefault();
    resetAll();
    setIsExpanded(!isExpanded);
  };

  const getButton = () => {
    return (
      <CollapseExpandButton
        onClick={iconOnClick}
        tooltipText={isExpanded ? 'collapse' : 'expand'}
        data-testid="group-icon-click"
      />
    );
  };

  return (
    <StyledCard data-testid="group" elevation={1} className={classes.group}>
      <CardHeader
        className={classes.groupHeader}
        titleTypographyProps={{
          'data-testid': 'group-title',
          style: { width: '100%' },
          variant: 'h5',
          align: 'center'
        }}
        title={title || `name missing (id: ${key})`}
        action={
          <>
            <PropertiesButton
              className={classes.icon}
              onClick={
                onPropertiesClick ||
                (() => console.log('properties clicked, no onPropertiesClick given...'))
              }
              data-testid="container-properties-icon-click"
            />
            {children ? getButton() : null}
          </>
        }
        subheader={
          <Typography className={classes.subheader} data-testid="group-subheader" component="div">
            {subheader}
          </Typography>
        }
        classes={{
          content: classes.headerContent
        }}
      />
      {children ? (
        <Collapse
          data-testid="expanded-group"
          in={isExpanded}
          timeout={{ enter: 200, exit: 250 }}
          unmountOnExit
        >
          <CardContent sx={{ paddingTop: 0 }}>
            <Grid container direction={'row'} className={classes.children}>
              <Grid item xs={12}>
                <div className={classes.spacer}></div>
              </Grid>
              <Grid item xs={12}>
                <Grid container style={{ marginTop: 0 }}>
                  {children}
                </Grid>
              </Grid>
            </Grid>
          </CardContent>
        </Collapse>
      ) : null}
    </StyledCard>
  );
};

export default GroupContainer;
